/* eslint-disable react/self-closing-comp */
import { ReactNode, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Folder,
  Check,
  Tag,
  CalendarDays,
  Pencil,
  Youtube,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import PostDetailCarousel from '@/components/Post/PostDetailCarousel';
import ProfilePicture from '@/components/ui/profile-picture';
import DescriptionWithLinks from '@/components/Post/DescriptionWithLinks';
import OnefinityOfficialIcon from '@/assets/onefinity_officials_badge.png';
import IconTooltip from '@/components/ui/icon-tooltip';
import { Card } from '@/components/ui/card';
import OnefinityDetailsCard from './OnefinityDetailsCard';
import ToolsDataCard from './ToolsDataCard';
import JobDetailsCard from './JobDetailsCard';
import JobSettingsCard from './JobSettingsCard';
import LicenceDetailsCard from './LicenseDetailsCard';
import { Separator } from '@/components/ui/separator';
import {
  filterAttachmentsForCarousel,
  filterAttachmentsByController,
  getAvailableControllers,
  videoSlideIndex,
} from '@/redux/post/post.utils';
import PostStatusBadge from '@/components/Post/PostStatusBadge';
import DifficultyBadge from '@/components/Post/DifficultyBadge';
import {
  IPostVisibility,
  IControllerType,
  IPostDetailResponse,
} from '@/redux/post/post.d';
import { Toggle } from '@/components/ui/toggle';
import { CONTROLLER_CONFIG } from '@/lib/controllerConfig';

/** Shown on the card. The full stamp below is the `title` for anyone who wants it. */
const DATE: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const STAMP: Intl.DateTimeFormatOptions = {
  ...DATE,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

interface PostDetailBodyProps {
  post: IPostDetailResponse;
  /**
   * Like / rate / bookmark / share. A slot rather than shared markup: the
   * preview is fetched with a token and no requester, so its payload carries no
   * hasLikedByUser or hasBookmarkedByUser to drive them.
   */
  interactions?: ReactNode;
  /** Download control, pinned right of the interactions. */
  download?: ReactNode;
  /** Owner-only menu, beside the status badge. */
  titleActions?: ReactNode;
  /** Admin-feedback opener, on its own row above the interactions. */
  notice?: ReactNode;
}

/**
 * The whole two-column project body: shot and controllers on the left, action
 * bar and spec cards on the right.
 *
 * Shared by the public detail page and the admin preview so the two cannot
 * drift — they had, and the preview was missing the action bar, the badges, the
 * dates and the tag list.
 */
// fallow-ignore-next-line complexity
export default function PostDetailBody({
  post,
  interactions,
  download,
  titleActions,
  notice,
}: PostDetailBodyProps) {
  const [modelIndex, setModelIndex] = useState<number>(0);

  const availableControllers = useMemo(
    () => getAvailableControllers(post?.attachments),
    [post?.attachments]
  );

  const [selectedController, setSelectedController] =
    useState<IControllerType | null>(null);

  useEffect(() => {
    if (availableControllers.length > 0 && !selectedController) {
      setSelectedController(availableControllers[0]);
    }
  }, [availableControllers, selectedController]);

  // fallow-ignore-next-line complexity
  const filteredAttachments = useMemo(() => {
    if (!selectedController) return post?.attachments || [];
    return filterAttachmentsByController(post?.attachments, selectedController);
  }, [post?.attachments, selectedController]);

  // the exact array the carousel renders, so the badge can't point at a missing slide
  const carouselVideoIndex = useMemo(
    () => videoSlideIndex(filterAttachmentsForCarousel(filteredAttachments)),
    [filteredAttachments]
  );

  useEffect(() => {
    setModelIndex(0);
  }, [selectedController]);

  const uploadedOn = new Date(post.createdAt).toLocaleDateString('en-US', DATE);
  const editedRaw = new Date(
    post.updatedAt || post.createdAt
  ).toLocaleDateString('en-US', DATE);
  const editedOn = editedRaw === uploadedOn ? null : editedRaw;

  return (
    <div className="mx-auto rounded-none border-none shadow-none px-5 py-[2rem]">
      <div className="flex flex-wrap gap-10 lg:gap-0 lg:flex-nowrap lg:items-start">
        <div className="flex flex-col lg:w-[50%] lg:border-r-[1px] border-[#E0E0E0] items-center justify-start gap-4 px-0 lg:px-8 w-full border-r-0 lg:sticky lg:top-[calc(var(--nav-height)_+_1rem)] lg:self-start">
          <div className="flex flex-col w-full gap-2">
            <div className="flex items-start justify-between gap-2">
              <h1 className="font-primary font-bold text-2xl capitalize">
                {post?.title}
              </h1>
              <div className="flex items-center gap-2 shrink-0">
                <PostStatusBadge
                  visibility={post.visibility as IPostVisibility}
                />
                {titleActions}
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Link to={`/${post?.user?.username}`}>
                <div className="flex items-center gap-2">
                  <ProfilePicture
                    src={post?.user?.profilePicture}
                    alt={post?.user?.firstname || post?.user?.username}
                    username={post?.user?.username}
                    variant="badge"
                    size="sm"
                  />
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-[#666666]">
                      @{post?.user?.username}
                    </span>
                    {post.user.isOnefinityOfficial ? (
                      <IconTooltip legendKey="onefinity-official">
                        <img
                          src={OnefinityOfficialIcon}
                          className="h-6 w-6"
                          alt="onefinity official"
                        />
                      </IconTooltip>
                    ) : (
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <></>
                    )}
                    {post.user.isTrustedMember && (
                      <IconTooltip legendKey="trusted-member">
                        <div
                          className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center"
                          aria-label="Trusted member"
                        >
                          <Check
                            className="h-3 w-3 text-white"
                            strokeWidth={3}
                          />
                        </div>
                      </IconTooltip>
                    )}
                  </div>
                </div>
              </Link>
              {post.projectGroup && (
                <Link
                  to={`/project-groups/${post.projectGroup._id}`}
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <Folder className="h-4 w-4" />
                  <span>{post.projectGroup.name}</span>
                </Link>
              )}
            </div>
          </div>
          <Separator className="lg:hidden" />
          {availableControllers.length > 0 && (
            <div className="flex gap-2 items-center justify-center flex-wrap w-full">
              {availableControllers.map((controllerType) => {
                const config = CONTROLLER_CONFIG[controllerType];
                const IconComponent = config.icon;
                return (
                  <Toggle
                    key={controllerType}
                    pressed={selectedController === controllerType}
                    onPressedChange={() =>
                      setSelectedController(controllerType)
                    }
                    className="gap-2 border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=off]:border-border"
                  >
                    {IconComponent && <IconComponent className="w-5 h-5" />}
                    <span>{config.name}</span>
                  </Toggle>
                );
              })}
            </div>
          )}
          <PostDetailCarousel
            attachments={filterAttachmentsForCarousel(filteredAttachments)}
            modelIndex={modelIndex}
            setModelIndex={setModelIndex}
          />
        </div>
        <div className="lg:w-[50%] w-full relative">
          {/* Action bar sticks only while the description + spec cards are in
              view, then scrolls away with the rest of the content. */}
          <div>
            <div className="sticky top-[var(--nav-height)] z-10 px-0 lg:px-[35px] py-3 mb-2">
              <Card className="flex flex-col gap-3 p-3 bg-transparent backdrop-blur">
                <div className="flex flex-wrap items-center gap-2 empty:hidden">
                  {notice}
                </div>
                <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center">
                  {/* ponytail: one wrapper strips the children's own borders so they read as a single segmented group */}
                  {/* The four labels never fit one 375px row; a 2x2 grid beats
                      the ragged wrap that left Share orphaned on its own line.
                      auto-fit, not grid-cols-2: Preview passes a single button. */}
                  {/* w-auto/px-6: icon-size children come in at a fixed 40px
                      with no padding, which breaks the row's rhythm. */}
                  <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-0.5 rounded-2xl border bg-white/50 p-0.5 [&>*:last-child:nth-child(odd)]:col-span-full [&_button]:w-auto [&_button]:rounded-full [&_button]:border-0 [&_button]:bg-transparent [&_button]:px-6 [&_button]:text-sm [&_button]:shadow-none [&_button:hover]:bg-white empty:hidden sm:flex sm:w-auto sm:items-center sm:rounded-full">
                    {interactions}
                  </div>
                  <div className="w-full sm:ml-auto sm:w-auto">{download}</div>
                </div>
                <div className="flex justify-between items-center flex-wrap gap-2 w-full">
                  <div className="flex flex-wrap items-center gap-2 empty:hidden">
                    {post.category && (
                      <Link
                        to={`/?category=${encodeURIComponent(post.category.name)}`}
                        title={`Browse ${post.category.name} projects`}
                      >
                        <Badge
                          variant="secondary"
                          className="gap-1.5 rounded-full px-3 py-1 font-medium capitalize cursor-pointer border border-border transition-colors hover:border-primary/60 hover:text-primary hover:bg-primary/5"
                        >
                          <Tag className="h-3.5 w-3.5" />
                          {post.category.name}
                        </Badge>
                      </Link>
                    )}
                    <DifficultyBadge difficulty={post.difficulty} />
                    {carouselVideoIndex >= 0 && (
                      <button
                        type="button"
                        title="Jump to the video"
                        onClick={() => setModelIndex(carouselVideoIndex)}
                      >
                        <Badge
                          variant="secondary"
                          className="gap-1.5 rounded-full px-3 py-1 font-medium capitalize cursor-pointer border border-border transition-colors hover:border-primary/60 hover:text-primary hover:bg-primary/5"
                        >
                          <Youtube className="h-3.5 w-3.5 text-[#FF0000]" />
                          Video
                        </Badge>
                      </button>
                    )}
                  </div>
                  <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/70 pt-2.5 text-xs text-muted-foreground sm:ml-auto sm:w-auto sm:border-t-0 sm:pt-0">
                    <span
                      className="inline-flex items-center gap-1.5"
                      title={new Date(post.createdAt).toLocaleString(
                        'en-US',
                        STAMP
                      )}
                    >
                      <CalendarDays className="h-3.5 w-3.5 flex-none" />
                      Uploaded
                      <span className="font-medium text-foreground">
                        {uploadedOn}
                      </span>
                    </span>
                    {/* Same-day edits are the norm right after upload; two
                        identical dates read as a rendering bug. */}
                    {editedOn && (
                      <span
                        className="inline-flex items-center gap-1.5"
                        title={new Date(
                          post.updatedAt || post.createdAt
                        ).toLocaleString('en-US', STAMP)}
                      >
                        <Pencil className="h-3.5 w-3.5 flex-none" />
                        Edited
                        <span className="font-medium text-foreground">
                          {editedOn}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            </div>
            <div className="px-0 lg:px-[35px]">
              <DescriptionWithLinks
                description={post?.description}
                footer={
                  post.tags.length > 0 && (
                    <p className="flex flex-wrap gap-x-2 gap-y-1 text-sm text-blue-500">
                      {post.tags.map((tag) => (
                        <span
                          key={tag._id}
                          className="cursor-pointer hover:underline"
                        >
                          #{tag.name.replace(/\s+/g, '')}
                        </span>
                      ))}
                    </p>
                  )
                }
              />
              <div className="mt-4 grid gap-6 sm:grid-cols-2">
                <JobDetailsCard post={post} />
                <JobSettingsCard post={post} />
              </div>
            </div>
          </div>
          <div className="px-0 lg:px-[35px]">
            <ToolsDataCard
              post={post}
              modelIndex={modelIndex}
              setModelIndex={setModelIndex}
              selectedController={selectedController}
              filteredAttachments={filteredAttachments}
            />
            <OnefinityDetailsCard post={post} />
            <div className="mt-6">
              <LicenceDetailsCard post={post} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
